"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.integrationCatalogueListQuerySchema = exports.integrationAssetListQuerySchema = exports.integrationDispatchListQuerySchema = exports.integrationLineStatusUpdateSchema = exports.integrationDispatchUpdateSchema = exports.integrationDispatchCreateSchema = exports.DISPATCH_JOB_STATUSES = exports.INTEGRATION_JOB_STATUSES = void 0;
const zod_1 = require("zod");
const common_1 = require("./common");
/**
 * Payload contracts for the partner integration API
 * (`/api/v1/integrations/dispatch-jobs`).
 *
 * Deliberately separate from `dispatchJobSchema` in `fleet-ops.ts`:
 *
 *   - That schema is the INTERNAL contract. It takes AFM ObjectIds, allows
 *     every field the model has, and is wrapped in two `ZodEffects` layers
 *     that `defaultUpdateSchema` (crud-factory.ts:27-32) peels apart. Editing
 *     it to suit a partner would perturb the admin UI's PUT validation.
 *   - This schema is the EXTERNAL contract. It takes the partner's own
 *     identifiers (fleet number, rego, employee email, catalogue item code),
 *     and refuses anything AFM owns.
 *
 * Both are `.strict()`. Silently dropping an unrecognised field is the worst
 * failure mode in an integration — the partner believes they sent it, and
 * nobody finds out until a month-end reconciliation.
 */
/**
 * Statuses the integration may set, exact-cased.
 *
 * `DispatchJob.status` is a free-form String at the schema level, but
 * `statusRank` (dispatch-job.model.ts:193-198) matches these strings exactly
 * to stamp `enRouteAt` / `arrivedAt` / `completedAt`. A lowercase 'complete'
 * would be stored happily and then silently never stamp `completedAt`, so the
 * job looks finished on the board and is invisible to every completion
 * report. The enum is the fix at the boundary.
 */
exports.INTEGRATION_JOB_STATUSES = [
    'Scheduled',
    'En route',
    'Arrived',
    'Complete',
    'Cancelled',
];
/** The same set, under the name internal code uses. Dispatch job line items
 *  (DispatchJobProduct.status) carry exactly these values too. */
exports.DISPATCH_JOB_STATUSES = exports.INTEGRATION_JOB_STATUSES;
/**
 * ISO-8601 **with an explicit offset or Z**. A naive `2026-08-18T09:30:00`
 * is rejected: the dispatch board is NZ wall-clock, and a bare timestamp
 * from an offshore caller lands up to 13 hours out with no error anywhere.
 */
const offsetDateTime = zod_1.z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/, 'Must be ISO-8601 with a UTC offset or Z, e.g. 2026-08-18T09:30:00+12:00');
const addressInput = zod_1.z
    .object({
    addressLine1: zod_1.z.string().max(200).nullable().optional(),
    addressLine2: zod_1.z.string().max(200).nullable().optional(),
    city: zod_1.z.string().max(120).nullable().optional(),
    stateProvince: zod_1.z.string().max(120).nullable().optional(),
    postalCode: zod_1.z.string().max(30).nullable().optional(),
    country: zod_1.z.string().max(120).nullable().optional(),
    latitude: zod_1.z.number().min(-90).max(90).nullable().optional(),
    longitude: zod_1.z.number().min(-180).max(180).nullable().optional(),
})
    .strict();
/** One product/material line. `itemCode` is `CatalogueItem.itemCode`, which is uniquely indexed. */
const lineInput = zod_1.z
    .object({
    itemCode: zod_1.z.string().min(1).max(120),
    quantity: zod_1.z.number().finite().nullable().optional(),
    unit: zod_1.z.string().max(40).nullable().optional(),
})
    .strict();
/** Fields common to create and update. */
const jobFields = {
    /** Partner's own reference. Namespaced by them, e.g. `WTX-12345`. Create-only. */
    externalRef: zod_1.z.string().min(1).max(120).optional(),
    // ── vehicle: exactly one of these three, resolved server-side ──
    assetId: common_1.objectIdSchema.optional(),
    fleetNumber: zod_1.z.string().min(1).max(60).optional(),
    rego: zod_1.z.string().min(1).max(30).optional(),
    // ── driver: NOT settable through this API ──
    // `driverId` went first: it took an AFM ObjectId that nothing in the partner
    // API ever exposed. `driverEmail` has now followed it. Assigning the driver
    // is Atlas's dispatch decision, not the partner's — a weighbridge knows which
    // vehicle and what was loaded, not who Atlas will roster onto the job. The
    // field also required a partner to hold staff email addresses, which is
    // internal personal data they have no reason to keep.
    //
    // The response still REPORTS `driver` once Atlas assigns one, so a partner
    // can display it; they simply cannot set it. Sending either field now
    // returns 400 UNKNOWN_FIELD naming it.
    customer: zod_1.z.string().max(200).nullable().optional(),
    jobDetails: zod_1.z.string().max(4000).nullable().optional(),
    phoneNumber: zod_1.z.string().max(60).nullable().optional(),
    completionNotes: zod_1.z.string().max(4000).nullable().optional(),
    status: zod_1.z.enum(exports.INTEGRATION_JOB_STATUSES).optional(),
    address: addressInput.nullable().optional(),
    lines: zod_1.z.array(lineInput).max(200).optional(),
};
exports.integrationDispatchCreateSchema = zod_1.z
    .object({
    ...jobFields,
    start: offsetDateTime,
    end: offsetDateTime.nullable().optional(),
})
    .strict()
    .refine((v) => !!(v.assetId || v.fleetNumber || v.rego), { message: 'One of assetId, fleetNumber or rego is required to identify the vehicle', path: ['fleetNumber'] });
exports.integrationDispatchUpdateSchema = zod_1.z
    .object({
    ...jobFields,
    start: offsetDateTime.optional(),
    end: offsetDateTime.nullable().optional(),
})
    .strict()
    // `externalRef` is the idempotency key: it identifies which of the partner's
    // events produced this job. Letting it move would break that mapping and
    // could collide with another job's ref.
    .refine((v) => v.externalRef === undefined, {
    message: 'externalRef is set at creation and cannot be changed',
    path: ['externalRef'],
})
    .refine((v) => Object.keys(v).length > 0, {
    message: 'Provide at least one field to update',
});
/**
 * Status update for ONE product line (`PUT /dispatch-job-lines/:id`).
 *
 * Each weighbridge line is its own Weightrax job — its AFM id is the
 * EXT_JOB_ID Weightrax holds — so the weighbridge reports outcomes per line.
 * Status (plus the weighed net weight on completion): quantity, unit and
 * product stay with whoever owns the job.
 * The parent job is rolled up server-side (Complete once every weighbridge
 * line is), never set from here.
 */
exports.integrationLineStatusUpdateSchema = zod_1.z
    .object({
    status: zod_1.z.enum(exports.DISPATCH_JOB_STATUSES),
    /** Weighed net weight in kg — sent with `status: 'Complete'`. */
    netWeightKg: zod_1.z.number().nonnegative().max(200000).optional(),
})
    .strict()
    .refine((v) => v.netWeightKg === undefined || v.status === 'Complete', {
    message: 'netWeightKg can only be sent with status "Complete"',
    path: ['netWeightKg'],
});
/**
 * Query contract for listing dispatch jobs.
 *
 * Every filter is optional, including `externalRef`. An earlier draft made
 * that field mandatory so the endpoint could only ever act as a
 * "did my POST land?" lookup — which reads as an API shaped around one
 * partner's retry logic rather than around dispatch jobs. Any consumer
 * needs to answer "what is on for tomorrow" or "what changed since my last
 * poll", and neither question has an `externalRef` to offer.
 *
 * Values arrive as strings from the query string, so numbers and booleans
 * are coerced here rather than in the route.
 */
exports.integrationDispatchListQuerySchema = zod_1.z
    .object({
    /** Exact match on the caller's own reference. */
    externalRef: zod_1.z.string().min(1).max(120).optional(),
    status: zod_1.z.enum(exports.INTEGRATION_JOB_STATUSES).optional(),
    // Vehicle, by any identifier the caller happens to hold.
    assetId: common_1.objectIdSchema.optional(),
    fleetNumber: zod_1.z.string().min(1).max(60).optional(),
    rego: zod_1.z.string().min(1).max(30).optional(),
    /** Half-open window on `start`: [startFrom, startTo). */
    startFrom: offsetDateTime.optional(),
    startTo: offsetDateTime.optional(),
    /**
     * Incremental sync: everything modified at or after this instant. The
     * companion to `sort=updatedAt&order=asc` — poll, take the last
     * `updatedAt` you saw, send it back next time.
     */
    updatedSince: offsetDateTime.optional(),
    page: zod_1.z.coerce.number().int().min(1).default(1),
    /** Capped: this collection is ~40k rows and grows. */
    limit: zod_1.z.coerce.number().int().min(1).max(200).default(50),
    sort: zod_1.z.enum(['start', 'updatedAt', 'createdAt']).default('start'),
    order: zod_1.z.enum(['asc', 'desc']).default('desc'),
})
    .strict()
    .refine((v) => !(v.startFrom && v.startTo) || v.startFrom <= v.startTo, { message: 'startFrom must be before startTo', path: ['startFrom'] });
/**
 * Query contract for listing assets.
 *
 * Partners need this to answer one question: "which vehicle do I mean?" —
 * turning a fleet number or plate they hold into an AFM id, and confirming
 * the vehicle is still on the road. It is not a fleet-management feed, which
 * is why the serialised shape is deliberately narrow.
 */
exports.integrationAssetListQuerySchema = zod_1.z
    .object({
    fleetNumber: zod_1.z.string().min(1).max(60).optional(),
    rego: zod_1.z.string().min(1).max(30).optional(),
    make: zod_1.z.string().min(1).max(120).optional(),
    model: zod_1.z.string().min(1).max(120).optional(),
    /** Substring match across fleet number, plate, make and model. */
    search: zod_1.z.string().min(1).max(120).optional(),
    updatedSince: offsetDateTime.optional(),
    page: zod_1.z.coerce.number().int().min(1).default(1),
    limit: zod_1.z.coerce.number().int().min(1).max(200).default(50),
    sort: zod_1.z.enum(['fleetNumber', 'updatedAt', 'createdAt']).default('fleetNumber'),
    order: zod_1.z.enum(['asc', 'desc']).default('asc'),
})
    .strict();
/**
 * Query contract for listing catalogue items.
 *
 * Exists because `lines[].itemCode` was otherwise unknowable. The create
 * contract requires a code that must already exist in AFM's catalogue — 1,581
 * of them — with no way for a caller to discover one. In practice that meant a
 * spreadsheet emailed once, which drifts the moment the catalogue changes, and
 * the drift surfaces as 422 CATALOGUE_ITEM_NOT_FOUND that the partner cannot
 * diagnose or fix themselves.
 */
exports.integrationCatalogueListQuerySchema = zod_1.z
    .object({
    /** Exact match, case-insensitive. */
    itemCode: zod_1.z.string().min(1).max(120).optional(),
    /** Substring across code and description. */
    search: zod_1.z.string().min(1).max(120).optional(),
    updatedSince: offsetDateTime.optional(),
    page: zod_1.z.coerce.number().int().min(1).default(1),
    limit: zod_1.z.coerce.number().int().min(1).max(200).default(50),
    sort: zod_1.z.enum(['itemCode', 'updatedAt', 'createdAt']).default('itemCode'),
    order: zod_1.z.enum(['asc', 'desc']).default('asc'),
})
    .strict();
//# sourceMappingURL=integrations.js.map